import {
  CRM_EMAIL_EVENT_FIELDS,
  isCrmEmailEventField,
  normalizeCrmEmailEventValue,
} from "./crmEmailEventFilters";
import { normalizeCrmCreatedDateValue } from "./crmCreatedDateFilters";

/** Approved Email & comms fields; each clause checks recorded send history. */
export const CRM_EMAIL_SOURCE_OPTIONS = [
  { value: "broadcast", label: "Broadcast" },
  { value: "automation", label: "Automation" },
  { value: "manual", label: "Manual" },
  { value: "system", label: "System" },
  { value: "test", label: "Test" },
] as const;
export const CRM_EMAIL_SOURCE_CONDITIONS = ["is", "is_not"] as const;

export const CRM_SENT_EMAIL_FIELDS = [
  { value: "email_template_sent", label: "Email template sent" },
  { value: "email_sent_date", label: "Email sent date" },
  { value: "email_source", label: "Email source" },
] as const;
export type CrmSentEmailField = (typeof CRM_SENT_EMAIL_FIELDS)[number]["value"];

export const CRM_SENT_EMAIL_TEMPLATE_CONDITIONS = [
  "is",
  "is_not",
  "has_happened",
  "has_not_happened",
] as const;
export const CRM_SENT_EMAIL_DATE_CONDITIONS = [
  "is_within",
  "before",
  "after",
] as const;
export type CrmSentEmailCondition =
  | (typeof CRM_SENT_EMAIL_TEMPLATE_CONDITIONS)[number]
  | (typeof CRM_SENT_EMAIL_DATE_CONDITIONS)[number];

export const isCrmSentEmailField = (
  field: string,
): field is CrmSentEmailField =>
  CRM_SENT_EMAIL_FIELDS.some((entry) => entry.value === field);

/**
 * Is/Is not select a persisted template identity, across versions.
 * Canonical operator grammar: Has happened/Has not happened take no value.
 */
export const normalizeCrmSentEmailValue = (
  field: string,
  condition: string,
  value: unknown,
): string | null => {
  if (field === "email_source") {
    if (
      !CRM_EMAIL_SOURCE_CONDITIONS.some((item) => item === condition) ||
      typeof value !== "string"
    )
      return null;
    return (
      CRM_EMAIL_SOURCE_OPTIONS.find(
        (item) => item.value === value.trim().toLowerCase(),
      )?.value ?? null
    );
  }
  if (field === "email_sent_date") {
    return CRM_SENT_EMAIL_DATE_CONDITIONS.some((item) => item === condition)
      ? normalizeCrmCreatedDateValue(condition, value)
      : null;
  }
  if (
    field === "email_template_sent" &&
    (condition === "has_happened" || condition === "has_not_happened")
  )
    return normalizeCrmEmailEventValue(condition, value);
  if (
    field !== "email_template_sent" ||
    !CRM_SENT_EMAIL_TEMPLATE_CONDITIONS.some((item) => item === condition) ||
    typeof value !== "string"
  )
    return null;
  const normalized = value.trim().toLowerCase();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u.test(
    normalized,
  )
    ? normalized
    : null;
};

/** All message-history fields share messaging-view permission and provenance. */
export const CRM_EMAIL_HISTORY_FIELDS = [
  ...CRM_SENT_EMAIL_FIELDS,
  ...CRM_EMAIL_EVENT_FIELDS,
] as const;
export const isCrmEmailHistoryField = (field: string): boolean =>
  isCrmSentEmailField(field) || isCrmEmailEventField(field);
